import type { PageEntry, Node } from 'contensis-delivery-api';
import htmlParser from 'html-react-parser';

import cmsParser from 'Common/util/cmsParser';
import { composerFacility, composerContentCarousel } from 'Common/util/cmsComposer';

import type { Props } from './Facility';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, ancestors } = data;

    const {
        title,
        sys,
        //Banner
        heroBannerImageDefault,
        // Virtual tour
        virtualTourHeading,
        virtualTourLink,
        // About section
        heading,
        description,
        // Quote
        quote,
        // Gallery
        galleryHeading,
        galleryCarousel,
        // Equipment
        equipmentHeading,
        equipmentList,
        // Courses
        coursesHeading,
        coursesList,
        // Research projects
        researchProjectsHeading,
        researchProject,
        // Button ctas
        buttons,
        // Hiring
        hireHeading,
        hireDescription,
        availability,
        contact
    } = entry;

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: {
            ...cmsParser.heroBannerWave(heroBannerImageDefault.heroBannerWaveDefault),
            image: cmsParser.image(heroBannerImageDefault.image)
        },
        tour: {
            heading: virtualTourHeading ? virtualTourHeading : null,
            link: virtualTourLink
                ? {
                      children: 'Virtual tour',
                      ariaLabel: 'Click to take a virtual tour',
                      href: virtualTourLink.url
                  }
                : null
        },
        about: {
            heading: heading,
            text: composerFacility.about(description),
            quote: quote
                ? {
                      children: quote.text,
                      cite: {
                          author: quote.source
                      },
                      quoteColorLeft: '#B9BCBC',
                      quoteColorRight: '#B9BCBC'
                  }
                : null
        },
        gallery: {
            heading: galleryHeading,
            carousel: {
                id: 'gallery',
                slides: composerContentCarousel.createSlides(galleryCarousel),
                slidesConfig: {
                    large: {
                        slidesToShow: 1,
                        gap: 0
                    },
                    medium: {
                        slidesToShow: 1,
                        gap: 0
                    },
                    small: {
                        slidesToShow: 1,
                        gap: 0
                    }
                }
            }
        },
        equipment: {
            heading: equipmentHeading,
            list: {
                items: equipmentList.item.map((item, index) => {
                    return {
                        id: index,
                        text: item
                    };
                }),
                columns: 2
            }
        },
        courses: {
            heading: coursesHeading,
            list: {
                items: coursesList.map(course => {
                    return {
                        id: course.sys.id,
                        text: `${
                            course.degreeType && course.degreeType !== 'none'
                                ? course.degreeType
                                : ''
                        } ${course.name}`
                    };
                })
            }
        },
        researchProjects: {
            heading: researchProjectsHeading,
            list: {
                items: researchProject.map((item, index) => {
                    return {
                        id: index,
                        text: item
                    };
                })
            }
        },
        ctas: buttons.map(button => {
            return {
                label: button.label,
                ariaLabel: button.ariaLabel,
                href: button.link.entryTitle,
                gradientVariant: 'accentFour'
            };
        }),
        hire: {
            heading: hireHeading ? hireHeading : '',
            description: hireDescription ? htmlParser(hireDescription) : '',
            availability: availability,
            button: {
                label: 'Find out more about external hire',
                ariaLabel: ' Click to email and find out more about external hire',
                href: `mailto:${contact && contact.email}`
            }
        }
    };
};

export default datamap;
